export const TICKET_ASSIGNEE_TYPE = {
  SALE: "sale",
  SALES_TEAM: "sales_team",
};

export const TICKET_ASSIGNEES_PER_PAGE = 20;

function getTicketExtValue(ticket) {
  const raw = ticket?.ext_value;

  if (raw && typeof raw === "object") {
    return raw;
  }

  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed = JSON.parse(raw);

      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  return {};
}

function formatTicketAssigneeTypeLabel(assigneeType) {
  if (assigneeType === TICKET_ASSIGNEE_TYPE.SALES_TEAM) {
    return "Sales team";
  }

  return "Sale";
}

function mapAssigneeToSelectOption({ type, id, name }) {
  const assigneeId = Number(id);

  if (!Number.isInteger(assigneeId) || assigneeId <= 0) {
    return null;
  }

  const displayName = String(name ?? "").trim() || String(assigneeId);

  return {
    value: `${type}:${assigneeId}`,
    label: `${displayName} - ${formatTicketAssigneeTypeLabel(type)}`,
    assigneeType: type,
    assigneeId,
  };
}

export function extractTicketAssigneesFromResponse(response) {
  const assignees = response?.data?.assignees;

  return Array.isArray(assignees) ? assignees : [];
}

export function resolveTicketAssigneesHasMore(response, { page, perPage }) {
  const meta = response?.data?._meta ?? {};
  const currentPage = Number(meta.currentPage ?? page);
  const pageCount = Number(meta.pageCount ?? 0);
  const assignees = extractTicketAssigneesFromResponse(response);

  if (pageCount > 0) {
    return currentPage < pageCount;
  }

  return assignees.length >= perPage;
}

export function mapTicketAssigneeListToSelectOptions(assignees = []) {
  return assignees
    .map(row =>
      mapAssigneeToSelectOption({
        type: row.type,
        id: row.id,
        name: row.name,
      })
    )
    .filter(Boolean);
}

export function validateTicketAssigneeSelections({
  assigneeSelections,
  requiredMessage,
}) {
  const errors = {};

  if (!(assigneeSelections?.length > 0)) {
    errors.assigneeSelections = requiredMessage;
  }

  return errors;
}

export function buildTicketAssigneesPayload({ assigneeSelections }) {
  const assignees = (assigneeSelections || [])
    .map(item => ({
      type: item.assigneeType,
      id: Number(item.assigneeId),
    }))
    .filter(
      item =>
        (item.type === TICKET_ASSIGNEE_TYPE.SALE ||
          item.type === TICKET_ASSIGNEE_TYPE.SALES_TEAM) &&
        Number.isInteger(item.id) &&
        item.id > 0
    );

  return { assignees };
}

/** Send-sale modal: pre-fill assignees from ticket.ext_value */
export function mapTicketRowToAssigneeSelections(ticket) {
  const ext = getTicketExtValue(ticket);
  const sales = Array.isArray(ext.sales) ? ext.sales : [];
  const teamIds = Array.isArray(ext.sales_team_ids) ? ext.sales_team_ids : [];
  const teamNames = Array.isArray(ext.sales_department_group_names)
    ? ext.sales_department_group_names
    : [];

  const saleOptions = sales
    .map(sale =>
      mapAssigneeToSelectOption({
        type: TICKET_ASSIGNEE_TYPE.SALE,
        id: sale?.id,
        name: sale?.name,
      })
    )
    .filter(Boolean);

  const teamOptions = teamIds
    .map((teamId, index) =>
      mapAssigneeToSelectOption({
        type: TICKET_ASSIGNEE_TYPE.SALES_TEAM,
        id: teamId,
        name: teamNames[index],
      })
    )
    .filter(Boolean);

  return [...saleOptions, ...teamOptions];
}
